import { useContext, useEffect, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  useMediaQuery,
  useTheme,
  Dialog,
  Tooltip,
} from '@mui/material';
import { ReactComponent as WaterBalanceIcon } from '../images/waterBalance.svg';
import './WaterBalanceMainModal.css';
import Draggable from 'react-draggable';
import { AppInfoContext } from '../../../../../App';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WaterBalanceContent from './WaterBalanceContent';

// must stay at module level, otherwise Paper remounts on every render
function PaperComponent(props) {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  if (isCompact) {
    return <Paper {...props} />;
  }

  return (
    <Draggable
      handle=".waterbalance-drag-handle"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <div
        style={{
          height: 'calc(100vh - 32px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
        }}
      >
        <Paper {...props} />
      </div>
    </Draggable>
  );
}

const WaterBalanceMainModal = () => {
  const { appInformation, setAppInformation } = useContext(AppInfoContext);
  const [isMinimized, setIsMinimized] = useState(false);
  const [modalAnimation, setModalAnimation] = useState('waterbalanceSlideOut');
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleCloseModal = () => {
    setAppInformation((prev) => ({
      ...prev,
      waterManagement: {
        ...prev.waterManagement,
        showWaterBalanceMainModal: false,
        waterBalanceData: null,
        waterBalanceVersion: 0,
      },
    }));
  }

  const toggleAnimation = () => {
    if (!isMinimized) {
      setModalAnimation('waterbalanceSlideIn');
      setIsMinimized(true);
    } else {
      setModalAnimation('waterbalanceSlideOut');
      setTimeout(() => {
        setIsMinimized(false);
      }, 600);
    }
  };

  useEffect(() => {
    if (
      appInformation.waterManagement.showWaterBalanceMainModal && isMinimized
    ) {
      setModalAnimation('waterbalanceSlideOut');
      setTimeout(() => {
        setIsMinimized(false);
      }, 600);
    }
  }, [
    appInformation.waterManagement.showWaterBalanceMainModal,
    appInformation.waterManagement.waterBalanceData?.dmaId,
    appInformation.waterManagement.waterBalanceVersion,
  ]);

  return (
    <>
      {!isMinimized && (
        <Dialog
          open={appInformation.waterManagement.showWaterBalanceMainModal}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperComponent={PaperComponent}
          hideBackdrop
          disableEnforceFocus
          disableAutoFocus
          disableRestoreFocus
          PaperProps={{
            className: isMinimized ? modalAnimation : '',
            sx: {
              // width must be paired with the margin, otherwise horizontal scroll
              margin: { xs: '8px', md: '16px' },
              maxHeight: { xs: 'calc(100% - 16px)', md: 'calc(100% - 32px)' },
              borderRadius: '0px',
              overflow: 'hidden',
              pointerEvents: isMinimized ? 'none' : 'auto',
              width: { xs: 'calc(100% - 16px)', md: 'calc(100% - 32px)' },
              transition: 'all 0.6s ease-in-out',

              '& .MuiDialogContent-root': {
                pointerEvents: 'auto',
              },

              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e1 transparent',

              '&::-webkit-scrollbar': {
                width: 6,
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#cbd5e1',
                borderRadius: 8,
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#94a3b8',
              },
            },
          }}
          sx={{ pointerEvents: 'none' }}
        >
          <Box
            className="waterbalance-modal-header waterbalance-drag-handle"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              // header has height: 0, padding gives the height; hence a fixed height below sm
              height: { xs: 55, sm: 0 },
              padding: isMobile ? '0 12px' : '22px',
              cursor: 'move',
              userSelect: 'none',
              pointerEvents: 'auto',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5 },
                minWidth: 0,
              }}
            >
              <Typography
                variant="h7"
                sx={{
                  fontSize: { xs: 13, sm: 'inherit' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {isMobile ? 'Bilans vode' : 'Bilans vode – IWA metodologija'}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  height: 20,
                  px: { xs: 0.75, sm: 1 },
                  borderRadius: '2px',
                  backgroundColor: '#f59e0b',
                  color: '#1f2937',
                  fontSize: { xs: 9.5, sm: 10.5 },
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                }}
              >
                {isMobile ? 'Demo' : 'Demo – testni podaci'}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Box className="waterbalance-modal-btn" onClick={toggleAnimation}>
                <RemoveRoundedIcon sx={{ fontSize: 18 }} />
              </Box>

              <Box className="waterbalance-modal-btn" onClick={handleCloseModal}>
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
            </Box>
          </Box>

          <WaterBalanceContent/>
          
        </Dialog>
      )}

      {isMinimized && (
        <Tooltip title="Bilans vode" arrow>
          <div
            className={`waterbalance-modal-min ${modalAnimation}`}
            onClick={toggleAnimation}
          >
          
            <WaterBalanceIcon
              style={{
                height: 20,
                width: 20,
                fill: '#fff',
              }}
            />
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default WaterBalanceMainModal;
